import { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../../contexts/AuthContext';
import strapiClient from '../../../services/strapi/config';
import SvgIcons from '../../../services/SvgIcons';
import './PurchasesPage.scss';

const paymentMethodLabel = {
    paypal: 'PayPal',
    carte:  'Carte bancaire'
};

const installmentStatusLabel = {
    pending:    '✅ Payé',
    scheduled:  '⏳ À venir',
    processing: '🔄 En cours',
    failed:     '❌ Échoué',
    completed:  '✅ Payé'
};

const purchaseStatusText = {
    confirme:   'Confirmé',
    en_attente: 'En attente',
    rembourse:  'Remboursé'
};

const PurchasesPage = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [purchases, setPurchases] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedPlan, setExpandedPlan] = useState(null);

    useEffect(() => {
        window.scroll(0, 0);
        if (!isAuthenticated) { navigate('/connexion'); return; }

        const fetchData = async () => {
            try {
                const token = localStorage.getItem('strapiToken');
                const [purchasesRes, plansRes] = await Promise.all([
                    strapiClient.get('/api/purchases/my-purchases', {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    strapiClient.get('/api/purchases/my-installment-plans', {
                        headers: { Authorization: `Bearer ${token}` }
                    }).catch(() => ({ data: { data: [] } }))
                ]);
                setPurchases(purchasesRes.data.data || []);
                setPlans(plansRes.data.data || []);
            } catch (err) {
                setError('Impossible de charger tes achats. Réessaie plus tard.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isAuthenticated, navigate]);

    const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric'
    });

    const formatAmount = (amount) => new Intl.NumberFormat('fr-FR', {
        style: 'currency', currency: 'EUR'
    }).format(amount);

    // Grouper par formation, puis par plan pour les échelonnés
    const formationGroups = useMemo(() => {
        const byFormation = {};
        purchases.forEach(p => {
            const key = p.formationSlug || p.formationAchetee;
            if (!byFormation[key]) byFormation[key] = { title: p.formationAchetee, singles: [], byPlan: {} };
            if (!p.paiementPlusieurs) {
                byFormation[key].singles.push(p);
            } else {
                const planKey = String(p.installmentPlanId || 'unknown');
                if (!byFormation[key].byPlan[planKey]) byFormation[key].byPlan[planKey] = [];
                byFormation[key].byPlan[planKey].push(p);
            }
        });
        // Trier les versements par date dans chaque plan
        Object.values(byFormation).forEach(({ byPlan }) => {
            Object.values(byPlan).forEach(arr =>
                arr.sort((a, b) => new Date(a.dateAchat) - new Date(b.dateAchat))
            );
        });
        return Object.values(byFormation);
    }, [purchases]);

    const togglePlan = (planId) => {
        setExpandedPlan(prev => prev === planId ? null : planId);
    };

    return (
        <div className="purchases-page">
            <Helmet>
                <title>Mes achats | Mon compte | Grandir et s'élever ensemble</title>
            </Helmet>

            <div className="purchases-page__container">
                <Link to="/mon-compte" className="purchases-page__back">
                    <SvgIcons name="chevron_back" width="20" height="20" color="currentColor" />
                    Mon compte
                </Link>
                <h1>Mes achats</h1>

                {loading && <p className="purchases-page__loading">Chargement...</p>}
                {error && <p className="purchases-page__error">{error}</p>}

                {!loading && !error && purchases.length === 0 && (
                    <p className="purchases-page__empty">
                        Tu n'as pas encore effectué d'achat.{' '}
                        <Link to="/ateliers">Découvrir les ateliers</Link>
                    </p>
                )}

                {!loading && formationGroups.length > 0 && (
                    <div className="purchases-page__formations">
                        {formationGroups.map((group) => (
                            <div key={group.title} className="purchases-page__formation-block">
                                <h2 className="purchases-page__formation-title">{group.title}</h2>

                                {/* Paiements uniques */}
                                {group.singles.map(p => (
                                    <div key={p.id} className="purchases-page__versement">
                                        <div className="purchases-page__versement-header">
                                            <span className="purchases-page__versement-label">Paiement unique</span>
                                            <span className={`purchases-page__status purchases-page__status--${p.statut}`}>
                                                {purchaseStatusText[p.statut] || p.statut}
                                            </span>
                                        </div>
                                        <div className="purchases-page__versement-details">
                                            <span className="purchases-page__order-number">{p.numeroCommande}</span>
                                            <span className="purchases-page__date">{formatDate(p.dateAchat)}</span>
                                            <span className="purchases-page__amount">{formatAmount(p.prix)}</span>
                                            {p.typePaiement && (
                                                <span className={`purchases-page__payment-method purchases-page__payment-method--${p.typePaiement}`}>
                                                    {paymentMethodLabel[p.typePaiement] || p.typePaiement}
                                                </span>
                                            )}
                                        </div>
                                        <small className="purchases-page__ref">Réf : {p.stripePaymentIntentId}</small>
                                    </div>
                                ))}

                                {/* Paiements échelonnés (groupés par plan) */}
                                {Object.entries(group.byPlan).map(([planId, planPurchases]) => {
                                    const plan = plans.find(pl => String(pl.id) === planId);
                                    const isExpanded = expandedPlan === planId;
                                    const total = planPurchases[0]?.nombreEcheances || planPurchases.length;

                                    return (
                                        <div key={planId} className="purchases-page__plan-group">
                                            {planPurchases.map((p, index) => (
                                                <div key={p.id} className="purchases-page__versement">
                                                    <div className="purchases-page__versement-header">
                                                        <span className="purchases-page__versement-label">
                                                            Versement {index + 1}/{total}
                                                        </span>
                                                        <span className={`purchases-page__status purchases-page__status--${p.statut}`}>
                                                            {purchaseStatusText[p.statut] || p.statut}
                                                        </span>
                                                    </div>
                                                    <div className="purchases-page__versement-details">
                                                        <span className="purchases-page__order-number">{p.numeroCommande}</span>
                                                        <span className="purchases-page__date">{formatDate(p.dateAchat)}</span>
                                                        <span className="purchases-page__amount">{formatAmount(p.montantEcheance || p.prix)}</span>
                                                    </div>
                                                    <small className="purchases-page__ref">Réf : {p.stripePaymentIntentId}</small>
                                                </div>
                                            ))}

                                            {/* Échéancier complet du plan */}
                                            {plan && (
                                                <div className="purchases-page__installments">
                                                    <button
                                                        className="purchases-page__installments-toggle"
                                                        onClick={() => togglePlan(planId)}
                                                    >
                                                        <span>Voir l'échéancier complet</span>
                                                        <SvgIcons
                                                            name={isExpanded ? 'arrow_up' : 'chevron_back'}
                                                            width="16" height="16"
                                                            color="currentColor"
                                                            transform={isExpanded ? undefined : 'rotate(-90deg)'}
                                                        />
                                                    </button>

                                                    {isExpanded && (
                                                        <ul className="purchases-page__installments-list">
                                                            {plan.installments.map((inst) => (
                                                                <li key={inst.installmentNumber}
                                                                    className={`purchases-page__installment purchases-page__installment--${inst.status}`}
                                                                >
                                                                    <span className="purchases-page__inst-num">
                                                                        {inst.installmentNumber}/{plan.totalInstallments}
                                                                    </span>
                                                                    <span className="purchases-page__inst-amount">
                                                                        {formatAmount(inst.amount)}
                                                                    </span>
                                                                    <span className="purchases-page__inst-date">
                                                                        {formatDate(inst.dueDate)}
                                                                    </span>
                                                                    <span className="purchases-page__inst-status">
                                                                        {installmentStatusLabel[inst.status] || inst.status}
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PurchasesPage;
